'use client'

import { useEffect, useState } from 'react'
import { Check, Zap, Building2, Rocket } from 'lucide-react'
import { useT } from '@/lib/useT'
import { useLanguage } from '@/app/LanguageContext'

// Flip to true to fall back to the static plan data below (e.g. if the
// /api/plans DB-backed source has an issue). Keep the hardcoded config
// in place so this is a one-line revert.
const USE_HARDCODED_PLANS = false

interface DbPlan {
  id: number
  name: string
  charge: number
  ctaText: string | null
  maxUsers: number | null
  currencyCode: string
  currencySymbol: string
  features: string[]
}

const plansConfig = [
  {
    id: 'starter',
    icon: Rocket,
    nameKey: 'pricing.starter',
    taglineKey: 'pricing.starterDesc',
    monthlyPrice: 49,
    annualPrice: 39,
    unitKey: 'pricing.perUserMonth',
    featureKeys: [
      'pricing.upTo10',
      'pricing.crmAndSalesModule',
      'pricing.financeBasic',
      'pricing.storage5GB',
      'pricing.emailSupport',
      'pricing.apiReadAccess',
      'pricing.standardReports',
    ],
    supportKey: 'pricing.emailSupport',
    ctaKey: 'pricing.startFreeTrial',
    featured: false,
    accentColor: '#003F47',
    badgeColor: '',
  },
  {
    id: 'professional',
    icon: Zap,
    nameKey: 'pricing.professional',
    taglineKey: 'pricing.professionalDesc',
    monthlyPrice: 149,
    annualPrice: 119,
    unitKey: 'pricing.perUserMonth',
    featureKeys: [
      'pricing.upTo100',
      'pricing.allStarterModules',
      'pricing.hrPayroll',
      'pricing.projectManagement',
      'pricing.inventoryWarehouse',
      'pricing.storage50GB',
      'pricing.fullApiAccess',
      'pricing.advancedReports',
      'pricing.automationWorkflows',
      'pricing.prioritySupport',
      'pricing.sso',
      'pricing.mfa',
    ],
    supportKey: 'pricing.prioritySupport',
    ctaKey: 'pricing.startFreeTrial',
    featured: true,
    accentColor: '#FFBD76',
    badgeColor: 'pricing.mostPopular',
  },
  {
    id: 'enterprise',
    icon: Building2,
    nameKey: 'pricing.enterprise',
    taglineKey: 'pricing.enterpriseDesc',
    monthlyPrice: null,
    annualPrice: null,
    unitKey: '',
    featureKeys: [
      'pricing.unlimitedUsers',
      'pricing.allModulesIncluded',
      'pricing.unlimitedStorage',
      'pricing.customIntegrations',
      'pricing.auditLogs',
      'pricing.customBranding',
      'pricing.fullDataExport',
      'pricing.dedicatedOnboarding',
      'pricing.onPremiseOption',
      'pricing.sla247',
      'pricing.saml',
      'pricing.mfa',
    ],
    supportKey: 'pricing.sla247',
    ctaKey: 'pricing.contactSales',
    featured: false,
    accentColor: '#003F47',
    badgeColor: '',
  },
]

function HardcodedPricingSection() {
  const t = useT()
  const { language } = useLanguage()
  const [annual, setAnnual] = useState(false)
  const isArabic = language === 'ar'

  return (
    <section
      id="pricing"
      className="py-24 bg-[#FFF9F2]"
      aria-labelledby="pricing-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">

        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FFBD76]">
            {t('pricing.badge')}
          </p>
          <h2
            id="pricing-heading"
            className="text-3xl md:text-[2.75rem] font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('pricing.heading')}
          </h2>
          <p className="max-w-xl text-base md:text-lg text-[#6C7377] leading-relaxed">
            {t('pricing.subheading')}
          </p>
        </div>

        {/* Monthly / Annual toggle */}
        <div className="flex items-center justify-center gap-4 mb-12 ltr">
          <span className={`text-sm font-medium transition-colors duration-[140ms] whitespace-nowrap ${!annual ? 'text-[#0A171D]' : 'text-[#9EA2A5]'}`}>
            {t('pricing.monthly')}
          </span>
          <button
            role="switch"
            aria-checked={annual}
            onClick={() => setAnnual(!annual)}
            className={`relative inline-flex h-7 w-14 rounded-[999px] border transition-colors duration-[220ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] flex-shrink-0 ${
              annual ? 'bg-[#003F47] border-[#003F47]' : 'bg-[#E2E4E5] border-[#C5C8C9]'
            }`}
          >
            <span
              className={`absolute top-1/2 h-5 w-5 rounded-full shadow-sm transition-all duration-[220ms] pointer-events-none -translate-y-1/2 ${
                isArabic
                  ? annual
                    ? 'right-[32px] bg-[#FFBD76]'
                    : 'right-1 bg-white'
                  : annual
                  ? 'left-[32px] bg-[#FFBD76]'
                  : 'left-1 bg-white'
              }`}
            />
            <span className="sr-only">{annual ? 'Annual billing active' : 'Monthly billing active'}</span>
          </button>
          <span className={`flex items-center gap-2 text-sm font-medium transition-colors duration-[140ms] whitespace-nowrap ${annual ? 'text-[#0A171D]' : 'text-[#9EA2A5]'}`}>
            {t('pricing.annual')}
            <span className="inline-flex items-center px-2 py-0.5 rounded-[999px] bg-[#003F47] text-[#FFFBF7] text-[11px] font-semibold tracking-wide">
              {t('pricing.save20')}
            </span>
          </span>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plansConfig.map((plan) => {
            const PlanIcon = plan.icon
            const displayPrice = annual ? plan.annualPrice : plan.monthlyPrice

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-[18px] border transition-all duration-[220ms] overflow-hidden
                  ${plan.featured
                    ? 'bg-[#003F47] border-[#003F47] shadow-[0_12px_32px_rgba(0,63,71,0.22)] scale-[1.02]'
                    : 'bg-white border-[#E2E4E5] shadow-[0_1px_2px_rgba(10,23,29,0.06),0_2px_6px_rgba(10,23,29,0.06)] hover:shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)] hover:-translate-y-0.5'
                  }`}
              >
                {/* Top accent bar */}
                {plan.featured && (
                  <div
                    className="h-1 w-full shrink-0"
                    style={{
                      background:
                        'linear-gradient(90deg,#E91E8C,#F7941D,#FFE000,#8DC63F,#27AE9A,#2E9BD6)',
                    }}
                    aria-hidden="true"
                  />
                )}

                {/* Badge */}
                {plan.badgeColor && (
                  <div className="absolute top-4 right-5">
                    <span className="px-3 py-1 rounded-[999px] bg-[#FFBD76] text-[#0A171D] text-[11px] font-bold uppercase tracking-[0.08em]">
                      {t(plan.badgeColor as any)}
                    </span>
                  </div>
                )}

                <div className="flex flex-col flex-1 gap-6 p-8">
                  {/* Icon + name */}
                  <div className="flex flex-col gap-3">
                    <div
                      className={`inline-flex items-center justify-center h-10 w-10 rounded-[12px] ${
                        plan.featured ? 'bg-[#FFBD76]/20' : 'bg-[#E4ECED]'
                      }`}
                    >
                      <PlanIcon
                        size={20}
                        strokeWidth={2}
                        className={plan.featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}
                        aria-hidden="true"
                      />
                    </div>
                    <div>
                      <h3
                        className={`text-lg font-bold tracking-tight leading-snug ${
                          plan.featured ? 'text-[#FFFBF7]' : 'text-[#0A171D]'
                        }`}
                      >
                        {t(plan.nameKey as any)}
                      </h3>
                      <p
                        className={`text-sm mt-1 leading-relaxed ${
                          plan.featured ? 'text-[#FFF6E9]/60' : 'text-[#6C7377]'
                        }`}
                      >
                        {t(plan.taglineKey as any)}
                      </p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex items-end gap-1.5 border-t border-b py-5 -mx-1 px-1
                    border-[rgba(255,255,255,0.08)]"
                    style={{
                      borderColor: plan.featured ? 'rgba(255,255,255,0.1)' : '#E2E4E5',
                    }}
                  >
                    {displayPrice !== null ? (
                      <>
                        <span
                          className={`text-[2.75rem] font-bold tracking-[-0.02em] leading-none ${
                            plan.featured ? 'text-[#FFBD76]' : 'text-[#0A171D]'
                          }`}
                        >
                          ${displayPrice}
                        </span>
                        <div className="flex flex-col mb-1 gap-0.5">
                          <span
                            className={`text-xs leading-tight ${
                              plan.featured ? 'text-[#FFF6E9]/50' : 'text-[#9EA2A5]'
                            }`}
                          >
                            {t(plan.unitKey as any)}
                          </span>
                          {annual && (
                            <span
                              className={`text-[11px] font-medium ${
                                plan.featured ? 'text-[#FFBD76]/70' : 'text-[#003F47]'
                              }`}
                            >
                              {t('pricing.billedAnnually')}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <span
                        className={`text-[2.75rem] font-bold tracking-[-0.02em] leading-none ${
                          plan.featured ? 'text-[#FFBD76]' : 'text-[#0A171D]'
                        }`}
                      >
                        {t('pricing.custom')}
                      </span>
                    )}
                  </div>

                  {/* Feature list */}
                  <ul className="flex flex-col gap-2.5 flex-1" role="list">
                    {plan.featureKeys.map((fKey) => (
                      <li key={fKey} className="flex items-start gap-2.5">
                        <span
                          className={`mt-0.5 inline-flex items-center justify-center h-4 w-4 shrink-0 rounded-full ${
                            plan.featured ? 'bg-[#FFBD76]/20' : 'bg-[#E4ECED]'
                          }`}
                        >
                          <Check
                            size={10}
                            strokeWidth={3}
                            className={plan.featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}
                            aria-hidden="true"
                          />
                        </span>
                        <span
                          className={`text-sm leading-snug ${
                            plan.featured ? 'text-[#FFF6E9]/80' : 'text-[#16242B]'
                          }`}
                        >
                          {t(fKey as any)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Support badge */}
                  <div
                    className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-[8px] ${
                      plan.featured
                        ? 'bg-[#FFFBF7]/10 text-[#FFF6E9]/70'
                        : 'bg-[#FFF6E9] text-[#6C7377]'
                    }`}
                  >
                    <span className="text-xs uppercase tracking-[0.08em] font-semibold">Support:</span>
                    <span>{t(plan.supportKey as any)}</span>
                  </div>

                  {/* CTA */}
                  <a
                    href="#signup"
                    className={`inline-flex items-center justify-center px-6 py-3 rounded-[999px] text-sm font-semibold transition-all duration-[220ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] active:translate-y-px ${
                      plan.featured
                        ? 'bg-[#FFBD76] text-[#0A171D] hover:bg-[#F0A24E] shadow-[0_8px_24px_rgba(255,189,118,0.35)]'
                        : 'bg-[#003F47] text-[#FFFBF7] hover:bg-[#00343B] shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)]'
                    }`}
                  >
                    {t(plan.ctaKey as any)}
                  </a>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footnote */}
        <p className="mt-8 text-center text-sm text-[#9EA2A5]">
          {t('pricing.footer')}{' '}
          <a href="#comparison" className="text-[#1A535A] hover:text-[#003F47] font-medium transition-colors duration-[140ms]">
            {t('pricing.compareFeatures')} &darr;
          </a>
        </p>
      </div>
    </section>
  )
}

function planIcon(index: number, total: number) {
  if (index === 0) return Rocket
  if (index === total - 1) return Building2
  return Zap
}

function DbPricingSection() {
  const t = useT()
  const [annual, setAnnual] = useState(false)
  const [plans, setPlans] = useState<DbPlan[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/plans', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load plans')
        return res.json()
      })
      .then((data) => {
        if (!cancelled) setPlans(data.plans)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const featuredIndex = plans
    ? plans.findIndex((p) => p.name.toLowerCase().includes('professional'))
    : -1

  return (
    <section
      id="pricing"
      className="py-24 bg-[#FFF9F2]"
      aria-labelledby="pricing-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">

        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FFBD76]">
            {t('pricing.badge')}
          </p>
          <h2
            id="pricing-heading"
            className="text-3xl md:text-[2.75rem] font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('pricing.heading')}
          </h2>
          <p className="max-w-xl text-base md:text-lg text-[#6C7377] leading-relaxed">
            {t('pricing.subheading')}
          </p>
        </div>

        {/* Monthly / Annual toggle */}
        <div className="flex items-center justify-center gap-4 mb-12 ltr">
          <span className={`text-sm font-medium transition-colors duration-[140ms] whitespace-nowrap ${!annual ? 'text-[#0A171D]' : 'text-[#9EA2A5]'}`}>
            {t('pricing.monthly')}
          </span>
          <button
            role="switch"
            aria-checked={annual}
            onClick={() => setAnnual(!annual)}
            className={`relative inline-flex h-7 w-14 rounded-[999px] border transition-colors duration-[220ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] flex-shrink-0 ${
              annual ? 'bg-[#003F47] border-[#003F47]' : 'bg-[#E2E4E5] border-[#C5C8C9]'
            }`}
          >
            <span
              className={`absolute top-1/2 h-5 w-5 rounded-full shadow-sm transition-all duration-[220ms] pointer-events-none -translate-y-1/2 ${
                annual ? 'left-[32px] bg-[#FFBD76]' : 'left-1 bg-white'
              }`}
            />
            <span className="sr-only">{annual ? 'Annual billing active' : 'Monthly billing active'}</span>
          </button>
          <span className={`flex items-center gap-2 text-sm font-medium transition-colors duration-[140ms] whitespace-nowrap ${annual ? 'text-[#0A171D]' : 'text-[#9EA2A5]'}`}>
            {t('pricing.annual')}
            <span className="inline-flex items-center px-2 py-0.5 rounded-[999px] bg-[#003F47] text-[#FFFBF7] text-[11px] font-semibold tracking-wide">
              {t('pricing.save20')}
            </span>
          </span>
        </div>

        {error && (
          <p className="text-center text-sm text-red-600">Could not load plans. Please try again later.</p>
        )}

        {!error && !plans && (
          <p className="text-center text-sm text-[#6C7377]">Loading plans…</p>
        )}

        {!error && plans && (
          <>
            {/* Cards */}
            <div
              className="grid grid-cols-1 gap-6 items-stretch"
              style={{ gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, minmax(0, 1fr))` }}
            >
                {plans.map((plan, i) => {
                  const PlanIcon = planIcon(i, plans.length)
                  const featured = i === featuredIndex
                  const displayPrice = annual ? Math.round(plan.charge * 0.8) : plan.charge
                  const supportFeature = plan.features.find((f) => /support/i.test(f))

                  return (
                    <div
                      key={plan.id}
                      className={`relative flex flex-col rounded-[18px] border transition-all duration-[220ms] overflow-hidden
                        ${featured
                          ? 'bg-[#003F47] border-[#003F47] shadow-[0_12px_32px_rgba(0,63,71,0.22)] scale-[1.02]'
                          : 'bg-white border-[#E2E4E5] shadow-[0_1px_2px_rgba(10,23,29,0.06),0_2px_6px_rgba(10,23,29,0.06)] hover:shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)] hover:-translate-y-0.5'
                        }`}
                    >
                      {/* Top accent bar */}
                      {featured && (
                        <div
                          className="h-1 w-full shrink-0"
                          style={{
                            background:
                              'linear-gradient(90deg,#E91E8C,#F7941D,#FFE000,#8DC63F,#27AE9A,#2E9BD6)',
                          }}
                          aria-hidden="true"
                        />
                      )}

                      {/* Badge */}
                      {featured && (
                        <div className="absolute top-4 right-5">
                          <span className="px-3 py-1 rounded-[999px] bg-[#FFBD76] text-[#0A171D] text-[11px] font-bold uppercase tracking-[0.08em]">
                            {t('pricing.mostPopular')}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-col flex-1 gap-6 p-8">
                        {/* Icon + name */}
                        <div className="flex flex-col gap-3">
                          <div
                            className={`inline-flex items-center justify-center h-10 w-10 rounded-[12px] ${
                              featured ? 'bg-[#FFBD76]/20' : 'bg-[#E4ECED]'
                            }`}
                          >
                            <PlanIcon
                              size={20}
                              strokeWidth={2}
                              className={featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}
                              aria-hidden="true"
                            />
                          </div>
                          <div>
                            <h3
                              className={`text-lg font-bold tracking-tight leading-snug ${
                                featured ? 'text-[#FFFBF7]' : 'text-[#0A171D]'
                              }`}
                            >
                              {plan.name}
                            </h3>
                          </div>
                        </div>

                        {/* Price */}
                        <div
                          className="flex items-end gap-1.5 border-t border-b py-5 -mx-1 px-1"
                          style={{ borderColor: featured ? 'rgba(255,255,255,0.1)' : '#E2E4E5' }}
                        >
                          <span
                            className={`text-[2.75rem] font-bold tracking-[-0.02em] leading-none ${
                              featured ? 'text-[#FFBD76]' : 'text-[#0A171D]'
                            }`}
                          >
                            {plan.currencyCode} {displayPrice}
                          </span>
                          <div className="flex flex-col mb-1 gap-0.5">
                            <span
                              className={`text-xs leading-tight ${
                                featured ? 'text-[#FFF6E9]/50' : 'text-[#9EA2A5]'
                              }`}
                            >
                              {t('pricing.perUserMonth')}
                            </span>
                            {annual && (
                              <span
                                className={`text-[11px] font-medium ${
                                  featured ? 'text-[#FFBD76]/70' : 'text-[#003F47]'
                                }`}
                              >
                                {t('pricing.billedAnnually')}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* User cap */}
                        <p
                          className={`text-xs font-medium -mt-4 ${
                            featured ? 'text-[#FFF6E9]/60' : 'text-[#6C7377]'
                          }`}
                        >
                          {plan.maxUsers ? `Up to ${plan.maxUsers} users` : 'Unlimited users'}
                        </p>

                        {/* Feature list */}
                        <ul className="flex flex-col gap-2.5 flex-1" role="list">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2.5">
                              <span
                                className={`mt-0.5 inline-flex items-center justify-center h-4 w-4 shrink-0 rounded-full ${
                                  featured ? 'bg-[#FFBD76]/20' : 'bg-[#E4ECED]'
                                }`}
                              >
                                <Check
                                  size={10}
                                  strokeWidth={3}
                                  className={featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}
                                  aria-hidden="true"
                                />
                              </span>
                              <span
                                className={`text-sm leading-snug ${
                                  featured ? 'text-[#FFF6E9]/80' : 'text-[#16242B]'
                                }`}
                              >
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>

                        {/* Support badge */}
                        {supportFeature && (
                          <div
                            className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-[8px] ${
                              featured
                                ? 'bg-[#FFFBF7]/10 text-[#FFF6E9]/70'
                                : 'bg-[#FFF6E9] text-[#6C7377]'
                            }`}
                          >
                            <span className="text-xs uppercase tracking-[0.08em] font-semibold">Support:</span>
                            <span>{supportFeature}</span>
                          </div>
                        )}

                        {/* CTA */}
                        <a
                          href="#signup"
                          className={`inline-flex items-center justify-center px-6 py-3 rounded-[999px] text-sm font-semibold transition-all duration-[220ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] active:translate-y-px ${
                            featured
                              ? 'bg-[#FFBD76] text-[#0A171D] hover:bg-[#F0A24E] shadow-[0_8px_24px_rgba(255,189,118,0.35)]'
                              : 'bg-[#003F47] text-[#FFFBF7] hover:bg-[#00343B] shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)]'
                          }`}
                        >
                          {plan.ctaText || (plan.name.toLowerCase().includes('enterprise') ? t('pricing.contactSales') : t('pricing.startFreeTrial'))}
                        </a>
                      </div>
                    </div>
                  )
                })}
            </div>

            {/* Footnote */}
            <p className="mt-8 text-center text-sm text-[#9EA2A5]">
              {t('pricing.footer')}{' '}
              <a href="#comparison" className="text-[#1A535A] hover:text-[#003F47] font-medium transition-colors duration-[140ms]">
                {t('pricing.compareFeatures')} &darr;
              </a>
            </p>
          </>
        )}
      </div>
    </section>
  )
}

export default function PricingSection() {
  return USE_HARDCODED_PLANS ? <HardcodedPricingSection /> : <DbPricingSection />
}
